import { DirectionProvider } from '@base-ui/react/direction-provider';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';

import { AbilityProvider } from '@/core/authz/ability-provider';

import type { Direction } from '@/core/i18n/direction';
import type { ComponentType, ReactNode } from 'react';

type AppProvidersProps = {
  queryClient: QueryClient;
  /** Feeds Base UI's direction context; the caller still owns `<html dir>`. */
  direction: Direction;
  /**
   * URL-state adapter, injected so tests can swap the testing adapter for the
   * production one. Defaults to the react-router adapter the app runs.
   */
  urlAdapter?: ComponentType<{ children: ReactNode }>;
  children: ReactNode;
};

/**
 * The single provider assembly shared by the app, the test harness, and
 * Storybook, so the stack cannot drift three ways. The router is deliberately
 * NOT here: a router cannot nest inside another router, and stories need a memory
 * router or a data router, so every caller passes its router as `children`. None
 * of these four providers needs route context, so the nesting is stable
 * regardless of what `children` is.
 */
export function AppProviders({
  queryClient,
  direction,
  urlAdapter: UrlAdapter = NuqsAdapter,
  children,
}: AppProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <AbilityProvider>
        <DirectionProvider direction={direction}>
          <UrlAdapter>{children}</UrlAdapter>
        </DirectionProvider>
      </AbilityProvider>
    </QueryClientProvider>
  );
}
